import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  Input,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { NgCommonModule, TypedFormControl } from '@principle-theorem/ng-shared';
import {
  IPatientMetadataConfiguration,
  PatientMetadataType,
  PatientMetadataValue,
} from '@principle-theorem/principle-core/interfaces';
import { ISelectOption } from '@principle-theorem/shared';
import { isNil } from 'lodash';
import { Observable, ReplaySubject } from 'rxjs';
import { take } from 'rxjs/operators';

@Component({
  selector: 'pr-edit-patient-metadata',
  templateUrl: './edit-patient-metadata.component.html',
  styleUrls: ['./edit-patient-metadata.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EditPatientMetadataComponent),
      multi: true,
    },
  ],
  imports: [
    MatCheckboxModule,
    MatIcon,
    MatInputModule,
    MatSelectModule,
    MatTooltip,
    NgCommonModule,
  ],
})
export class EditPatientMetadataComponent implements ControlValueAccessor {
  private _onChange: (_: unknown) => void;
  private _onTouched: () => void;
  configuration$ = new ReplaySubject<IPatientMetadataConfiguration>(1);
  options: ISelectOption<PatientMetadataValue>[] = [];
  metadataType = PatientMetadataType;
  formattedDisplay$: Observable<string | undefined>;
  formCtrl = new TypedFormControl<
    PatientMetadataValue | PatientMetadataValue[] | undefined
  >(undefined);

  @Input()
  set configuration(configuration: IPatientMetadataConfiguration) {
    if (configuration) {
      this.configuration$.next(configuration);
    }
  }

  constructor() {
    this.configuration$
      .pipe(take(1), takeUntilDestroyed())
      .subscribe((configuration) => {
        if (configuration.options) {
          this.options = configuration.options;
        }

        if (!isNil(this.formCtrl.value)) {
          return;
        }

        const defaultValue = configuration.multiple
          ? configuration.defaultValue ?? []
          : configuration.defaultValue ?? undefined;
        this.formCtrl.setValue(defaultValue);
      });

    this.formCtrl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((newValue) => {
        if (this._onTouched) {
          this._onTouched();
        }

        if (this._onChange) {
          this._onChange(newValue);
        }
      });
  }

  writeValue(
    value: PatientMetadataValue | PatientMetadataValue[] | undefined
  ): void {
    if (!isNil(value)) {
      this.formCtrl.setValue(value);
    }
  }

  registerOnChange(fn: () => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }
}
